import { MouseEventHandler, useState } from "react";
import { Button } from "@/atoms-react/button/Button.react";
import { StoreButtons } from "@/atoms-react/stores-buttons/index.react";
import { Base, Info, SectionTitle } from "@/atoms-react/text/Text.react";
import { useSecurity } from "@/features/security/hooks/use-react-security";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { QRCodeCanvas } from "qrcode.react"; // Use a QR code library for React
import { InputCopiable } from "@/atoms-react/input/Copiable.react";
import CodeInput from "@/atoms-react/input/CodeInput.react";
import { Alert, AlertType } from "@/atoms-react/alert/index.react.tsx";
import { Themes } from "@/types/theme.ts";

type Security2faConfigProps = {
  onComplete: MouseEventHandler<HTMLButtonElement>;
};
export const Security2faConfig = ({ onComplete }: Security2faConfigProps) => {
  const { t } = useTranslation(["pass"]);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [qrContent, setQrContent] = useState("");
  const [secret, setSecret] = useState("");
  const { useActivate2fa, useSetup2fa } = useSecurity();
  const { mutateAsync: activate2fa } = useActivate2fa();
  const { mutateAsync: setup2fa } = useSetup2fa();
  const [step, setStep] = useState(0);

  const validateOtp = async (code: string) => {
    setOtp(code);
    setLoading(true);
    try {
      if (await activate2fa({ otp: code, secret: secret })) {
        setStep((prevStep) => prevStep + 1);
        toast.success(
          t("olkypass.securityPage.passwordSection.2fa.modal.notifications.otp.success")
        );
      } else {
        throw new Error("Invalid OTP");
      }
    } catch (_) {
      toast.error(t("olkypass.securityPage.passwordSection.2fa.modal.notifications.otp.error"));
    }
    setLoading(false);
  };

  return (
    <div>
      {step === 0 && (
        <div>
          <Alert
            type={AlertType.Warning}
            title={t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.important")}
          >
            <Info className="mt-2 block">
              {t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.message1")}
            </Info>
            <Info className="mt-2 block">
              {t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.message2")}
            </Info>
          </Alert>

          <Base className="mt-4 block">Google Authenticator</Base>
          <StoreButtons
            className="mt-2 mr-2"
            theme="google-btn"
            src="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
          />
          <StoreButtons
            className="mt-2 mr-2"
            theme="apple-btn"
            src="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8"
          />
          <Info className="mt-2 block">
            {t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.message3")}
          </Info>

          <Button
            loading={loading}
            className="float-right mt-4 max-w-48"
            dataCy={`button-2fa-next`}
            onClick={async () => {
              setLoading(true);
              try {
                const res = await setup2fa();
                setSecret(res?.secret || "");
                setQrContent(res?.qrContent || "");
                if (res?.secret) setStep((prevStep) => prevStep + 1);
              } catch (_) {
                toast.error("An error occurred");
              }
              setLoading(false);
            }}
          >
            {t("olkypass.general.next")}
          </Button>
        </div>
      )}
      {step === 1 && (
        <div>
          <SectionTitle>
            {t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.step1_title")}
          </SectionTitle>

          <Info>{t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.step1")}</Info>

          <div className="text-center">
            <div className="mx-auto my-2 inline-block overflow-hidden rounded-sm border border-slate-200 bg-white">
              <QRCodeCanvas value={qrContent} size={128} />
            </div>
          </div>

          <Info className="mb-2">
            {t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.step1b")}
          </Info>

          <InputCopiable value={secret} type="password" />
          <br />
          <SectionTitle>
            {t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.step2_title")}
          </SectionTitle>

          <Info>{t("olkypass.securityPage.passwordSection.2fa.modal.help.activation.step2")}</Info>

          <CodeInput
            className={"mt-2"}
            codeNumberOfCharacters={6}
            focus={true}
            onChange={(code) => validateOtp(code)} // Update OTP state
            dataCy="input-otp-code"
          />

          <Button
            loading={loading}
            disabled={otp.length < 6}
            className="float-right mt-8 max-w-48"
            onClick={async () => validateOtp(otp)}
          >
            {t("olkypass.securityPage.passwordSection.2fa.modal.button.enable")}
          </Button>
          <Button
            className="float-left mt-8 max-w-48"
            theme={Themes.white}
            onClick={() => setStep((prevStep) => prevStep - 1)}
          >
            {t("olkypass.general.previous")}
          </Button>
        </div>
      )}
      {step >= 2 && (
        <div>
          <Info className="block">
            {t("olkypass.securityPage.passwordSection.2fa.modal.notifications.otp.success")}
          </Info>
          <Button
            className="float-right mt-4 max-w-48"
            onClick={onComplete}
            dataCy="button-close-2fa-valid"
          >
            {t("olkypass.general.close")}
          </Button>
        </div>
      )}
    </div>
  );
};
